<template>
  <div class="text-wrapper">
    <h2 class="section-title">GIF 加文字</h2>

    <!-- 加载提示 -->
    <loading-tip 
      :visible="isLoading" 
      :text="loadingText" 
      :subtext="loadingSubtext"
      :progress="loadingProgress"
    />

    <!-- 上传区域 -->
    <label v-if="!hasGif" class="drop-area" :class="{ hover: isDragging }" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
      拖拽或点击上传 GIF
      <div class="size-hint">{{ isMobile ? '建议不超过 8MB' : '建议不超过 20MB' }}</div>
      <input type="file" ref="gifInput" accept="image/gif" @change="handleFileChange">
    </label>

    <!-- 预览区域 -->
    <div v-if="hasGif" class="preview-section">
      <div class="preview-header">
        <span class="preview-title">预览效果 <span class="drag-hint">（拖动文字调整位置）</span></span>
        <button class="btn-reselect" @click="resetAll">重新选择</button>
      </div>
      <div class="preview-canvas-wrapper" ref="canvasWrapper">
        <canvas 
          ref="previewCanvas" 
          class="preview-canvas"
          :class="{ dragging: isDraggingText }"
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
          @touchstart="startDragTouch"
          @touchmove="onDragTouch"
          @touchend="endDrag"
        ></canvas>
      </div>
    </div>

    <!-- 文字设置 -->
    <div v-if="hasGif" class="text-options">
      <div class="option-title">文字设置</div>
      
      <div class="option-group">
        <label class="option-label">文字内容</label>
        <input type="text" v-model="textContent" class="text-input" placeholder="输入要添加的文字" @input="updatePreview">
      </div>

      <div class="option-row">
        <div class="option-group half">
          <label class="option-label">字体大小</label>
          <input type="number" v-model.number="fontSize" min="8" max="100" class="number-input" @change="updatePreview">
        </div>
        <div class="option-group half">
          <label class="option-label">文字颜色</label>
          <input type="color" v-model="textColor" class="color-input" @change="updatePreview">
        </div>
      </div>

      <div class="option-row">
        <div class="option-group half">
          <label class="option-label">描边颜色</label>
          <input type="color" v-model="strokeColor" class="color-input" @change="updatePreview">
        </div>
        <div class="option-group half">
          <label class="option-label">描边粗细</label>
          <input type="number" v-model.number="strokeWidth" min="0" max="10" class="number-input" @change="updatePreview">
        </div>
      </div>

      <div class="option-row">
        <div class="option-group half">
          <label class="option-label">X 位置</label>
          <input type="number" v-model.number="textX" class="number-input" @change="updatePreview">
        </div>
        <div class="option-group half">
          <label class="option-label">Y 位置</label>
          <input type="number" v-model.number="textY" class="number-input" @change="updatePreview">
        </div>
      </div>
      
      <div class="quick-position">
        <span class="quick-label">快速定位:</span>
        <button class="quick-btn" @click="quickPosition('top')">顶部</button>
        <button class="quick-btn" @click="quickPosition('center')">居中</button>
        <button class="quick-btn" @click="quickPosition('bottom')">底部</button>
      </div>

      <div class="option-group">
        <label class="option-label">字体样式</label>
        <select v-model="fontFamily" class="select-input" @change="updatePreview">
          <option value="Arial">Arial</option>
          <option value="Microsoft YaHei">微软雅黑</option>
          <option value="SimHei">黑体</option>
          <option value="SimSun">宋体</option>
          <option value="KaiTi">楷体</option>
          <option value="Impact">Impact</option>
          <option value="Comic Sans MS">Comic Sans</option>
        </select>
      </div>

      <div class="option-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="isBold" @change="updatePreview">
          <span>粗体</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" v-model="isItalic" @change="updatePreview">
          <span>斜体</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" v-model="hasShadow" @change="updatePreview">
          <span>阴影</span>
        </label>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div v-if="hasGif" class="action-row">
      <button class="btn-primary" :disabled="isProcessing || !textContent" @click="startProcess">
        {{ isProcessing ? '正在处理...' : '生成 GIF' }}
      </button>
      <button class="btn-download" :disabled="!resultGifUrl" @click="downloadGif">下载 GIF</button>
    </div>

    <!-- 进度条 -->
    <div class="progress-container" v-if="isProcessing">
      <div class="progress-label">正在添加文字... {{ processProgress }}%</div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: processProgress + '%' }"></div>
      </div>
    </div>

    <!-- 结果预览 -->
    <div v-if="resultGifUrl" class="result-section">
      <div class="result-title">生成结果</div>
      <img :src="resultGifUrl" alt="添加文字后的 GIF" class="result-gif">
    </div>
  </div>
</template>

<script>
import SuperGif from '../lib/libgif.js'
import GIF from 'gif.js'
import LoadingTip from './LoadingTip.vue'
import { isMobile, getDeviceConfig, checkFileSize, formatFileSize, yieldToMain, downloadFile, blobToBase64 } from './utils.js'

export default {
  name: 'GifText',
  components: {
    LoadingTip
  },
  data() {
    const config = getDeviceConfig()
    return {
      gifUrl: '',
      gifFile: null,
      gifName: '',
      hasGif: false,
      isDragging: false,
      frames: [],
      frameDelays: [],
      originalWidth: 0,
      originalHeight: 0,
      
      // 流式处理
      superGif: null,
      gifWrapper: null,
      blobUrl: null,
      totalFrames: 0,
      
      // 文字设置
      textContent: '',
      fontSize: 24,
      textColor: '#ffffff',
      strokeColor: '#000000',
      strokeWidth: 2,
      textX: 0,
      textY: 0,
      fontFamily: 'Arial',
      isBold: true,
      isItalic: false,
      hasShadow: true,
      
      // 拖拽相关
      isDraggingText: false,
      dragStartX: 0,
      dragStartY: 0,
      canvasScale: 1,
      
      // 动画播放相关
      currentFrameIndex: 0,
      animationTimer: null,
      isPlaying: true,
      
      // 处理状态
      isProcessing: false,
      processProgress: 0,
      resultGifUrl: '',
      
      // 加载状态
      isLoading: false,
      loadingText: '',
      loadingSubtext: '',
      loadingProgress: -1,
      
      // 设备配置
      isMobile: config.isMobile,
      maxGifSize: config.maxGifSize,
      batchSize: config.batchSize
    }
  },
  methods: {
    handleDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file && file.type === 'image/gif') {
        this.processFile(file)
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    processFile(file) {
      // 文件大小检查
      const check = checkFileSize(file, this.maxGifSize)
      if (!check.valid) {
        this.$toast(check.message)
        return
      }
      
      this.gifFile = file
      this.gifName = file.name.replace(/\.gif$/i, '')
      this.gifUrl = URL.createObjectURL(file)
      this.hasGif = true
      this.resultGifUrl = ''
      
      // 显示加载提示
      this.isLoading = true
      this.loadingText = '正在解析 GIF...'
      this.loadingSubtext = `文件大小: ${formatFileSize(file.size)}`
      this.loadingProgress = -1
      
      this.parseGifStream(file)
    },
    parseGifStream(file) {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position:fixed;left:-9999px;top:-9999px;'
      
      const gifImg = document.createElement('img')
      const blobUrl = URL.createObjectURL(file)
      gifImg.setAttribute('rel:animated_src', blobUrl)
      gifImg.setAttribute('rel:auto_play', '0')
      
      wrapper.appendChild(gifImg)
      document.body.appendChild(wrapper)
      
      const rub = new SuperGif({ gif: gifImg })
      rub.load(() => {
        this.totalFrames = rub.get_length()
        this.frames = []
        this.frameDelays = []
        
        const canvas = rub.get_canvas()
        this.originalWidth = canvas.width
        this.originalHeight = canvas.height
        
        const framesData = rub.get_frames()
        
        // 对于预览，只存储少量帧用于动画预览
        const maxPreviewFrames = this.isMobile ? 30 : 50
        const step = Math.max(1, Math.floor(this.totalFrames / maxPreviewFrames))
        
        for (let i = 0; i < this.totalFrames; i++) {
          rub.move_to(i + 1)
          
          let delay = 100
          if (framesData && framesData[i] && framesData[i].delay) {
            delay = framesData[i].delay * 10
          }
          this.frameDelays.push(delay < 20 ? 100 : delay)
          
          // 只存储预览帧
          if (i % step === 0 || i === this.totalFrames - 1) {
            const frameCanvas = rub.get_canvas()
            const tempCanvas = document.createElement('canvas')
            tempCanvas.width = frameCanvas.width
            tempCanvas.height = frameCanvas.height
            const ctx = tempCanvas.getContext('2d')
            ctx.drawImage(frameCanvas, 0, 0)
            this.frames.push({
              index: i,
              dataUrl: tempCanvas.toDataURL('image/png')
            })
            // 释放临时 canvas
            tempCanvas.width = 1
            tempCanvas.height = 1
          }
        }
        
        // 保存 SuperGif 实例
        this.superGif = rub
        this.gifWrapper = wrapper
        this.blobUrl = blobUrl
        
        this.isLoading = false
        console.log(`GIF 解析完成: ${this.totalFrames} 帧, 预览帧: ${this.frames.length}`)
        
        // 初始化预览和默认文字位置
        this.$nextTick(() => {
          // 默认文字位置在底部居中
          this.textX = Math.round(this.originalWidth / 2)
          this.textY = Math.round(this.originalHeight - 30)
          this.startAnimation()
        })
      })
    },
    startAnimation() {
      // 停止之前的动画
      this.stopAnimation()
      
      if (!this.frames.length) return
      
      this.currentFrameIndex = 0
      this.isPlaying = true
      this.playNextFrame()
    },
    stopAnimation() {
      if (this.animationTimer) {
        clearTimeout(this.animationTimer)
        this.animationTimer = null
      }
    },
    playNextFrame() {
      if (!this.isPlaying || !this.frames.length) return
      
      this.renderFrame(this.currentFrameIndex)
      
      // 获取当前预览帧对应的原始帧索引，用于获取延迟
      const frameInfo = this.frames[this.currentFrameIndex]
      const originalIndex = frameInfo ? frameInfo.index : this.currentFrameIndex
      const delay = this.frameDelays[originalIndex] || 100
      
      // 下一帧
      this.currentFrameIndex = (this.currentFrameIndex + 1) % this.frames.length
      
      // 延迟后播放下一帧
      this.animationTimer = setTimeout(() => {
        this.playNextFrame()
      }, delay)
    },
    renderFrame(index) {
      const canvas = this.$refs.previewCanvas
      const frameInfo = this.frames[index]
      if (!canvas || !frameInfo) return
      
      canvas.width = this.originalWidth
      canvas.height = this.originalHeight
      const ctx = canvas.getContext('2d')
      
      // 计算缩放比例
      this.$nextTick(() => {
        const rect = canvas.getBoundingClientRect()
        if (rect.width > 0) {
          this.canvasScale = this.originalWidth / rect.width
        }
      })
      
      const img = new Image()
      img.onload = () => {
        ctx.drawImage(img, 0, 0)
        this.drawText(ctx, canvas.width, canvas.height)
      }
      img.src = frameInfo.dataUrl
    },
    updatePreview() {
      // 如果正在播放动画，只需要等待下一帧自动更新
      // 否则重新渲染当前帧
      if (!this.isPlaying) {
        this.renderFrame(this.currentFrameIndex)
      }
    },
    quickPosition(pos) {
      switch (pos) {
        case 'top':
          this.textX = Math.round(this.originalWidth / 2)
          this.textY = 30 + this.fontSize / 2
          break
        case 'center':
          this.textX = Math.round(this.originalWidth / 2)
          this.textY = Math.round(this.originalHeight / 2)
          break
        case 'bottom':
          this.textX = Math.round(this.originalWidth / 2)
          this.textY = Math.round(this.originalHeight - 30)
          break
      }
      this.updatePreview()
    },
    // 拖拽相关方法
    startDrag(e) {
      if (!this.textContent) return
      this.isDraggingText = true
      const rect = this.$refs.previewCanvas.getBoundingClientRect()
      this.dragStartX = (e.clientX - rect.left) * this.canvasScale
      this.dragStartY = (e.clientY - rect.top) * this.canvasScale
    },
    startDragTouch(e) {
      if (!this.textContent) return
      e.preventDefault()
      const touch = e.touches[0]
      this.isDraggingText = true
      const rect = this.$refs.previewCanvas.getBoundingClientRect()
      this.dragStartX = (touch.clientX - rect.left) * this.canvasScale
      this.dragStartY = (touch.clientY - rect.top) * this.canvasScale
    },
    onDrag(e) {
      if (!this.isDraggingText) return
      const rect = this.$refs.previewCanvas.getBoundingClientRect()
      const x = (e.clientX - rect.left) * this.canvasScale
      const y = (e.clientY - rect.top) * this.canvasScale
      
      this.textX = Math.round(Math.max(0, Math.min(this.originalWidth, x)))
      this.textY = Math.round(Math.max(0, Math.min(this.originalHeight, y)))
      this.updatePreview()
    },
    onDragTouch(e) {
      if (!this.isDraggingText) return
      e.preventDefault()
      const touch = e.touches[0]
      const rect = this.$refs.previewCanvas.getBoundingClientRect()
      const x = (touch.clientX - rect.left) * this.canvasScale
      const y = (touch.clientY - rect.top) * this.canvasScale
      
      this.textX = Math.round(Math.max(0, Math.min(this.originalWidth, x)))
      this.textY = Math.round(Math.max(0, Math.min(this.originalHeight, y)))
      this.updatePreview()
    },
    endDrag() {
      this.isDraggingText = false
    },
    drawText(ctx, width, height) {
      if (!this.textContent) return
      
      const x = this.textX
      const y = this.textY
      
      // 设置字体
      let fontStyle = ''
      if (this.isItalic) fontStyle += 'italic '
      if (this.isBold) fontStyle += 'bold '
      ctx.font = `${fontStyle}${this.fontSize}px "${this.fontFamily}"`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      
      // 绘制阴影
      if (this.hasShadow) {
        ctx.save()
        ctx.shadowColor = 'rgba(0,0,0,0.5)'
        ctx.shadowBlur = 4
        ctx.shadowOffsetX = 2
        ctx.shadowOffsetY = 2
        ctx.fillStyle = this.textColor
        ctx.fillText(this.textContent, x, y)
        ctx.restore()
      }
      
      // 绘制描边
      if (this.strokeWidth > 0) {
        ctx.strokeStyle = this.strokeColor
        ctx.lineWidth = this.strokeWidth
        ctx.lineJoin = 'round'
        ctx.strokeText(this.textContent, x, y)
      }
      
      // 绘制文字
      ctx.fillStyle = this.textColor
      ctx.fillText(this.textContent, x, y)
    },
    async startProcess() {
      if (!this.superGif || this.totalFrames === 0 || !this.textContent) {
        this.$toast('请先上传 GIF 并输入文字')
        return
      }
      
      this.isProcessing = true
      this.processProgress = 0
      this.resultGifUrl = ''
      
      const gif = new GIF({
        workers: this.isMobile ? 1 : 2,
        quality: this.isMobile ? 15 : 10,
        workerScript: './static/gifjs/gif.worker.js',
        width: this.originalWidth,
        height: this.originalHeight
      })
      
      gif.on('progress', (p) => {
        this.processProgress = Math.round(50 + p * 50)
      })
      
      // 创建可复用的 canvas
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = this.originalWidth
      tempCanvas.height = this.originalHeight
      const tempCtx = tempCanvas.getContext('2d')
      
      gif.on('finished', async (blob) => {
        // 使用data URL而不是blob URL，这样在小程序webview中可以长按保存
        this.resultGifUrl = await blobToBase64(blob)
        this.isProcessing = false
        this.processProgress = 100
        
        // 释放 canvas 内存
        tempCanvas.width = 1
        tempCanvas.height = 1
      })
      
      // 流式处理每一帧 - 直接从 SuperGif 读取
      for (let i = 0; i < this.totalFrames; i++) {
        this.superGif.move_to(i + 1)
        const frameCanvas = this.superGif.get_canvas()
        
        // 绘制原帧
        tempCtx.clearRect(0, 0, this.originalWidth, this.originalHeight)
        tempCtx.drawImage(frameCanvas, 0, 0)
        
        // 添加文字
        this.drawText(tempCtx, tempCanvas.width, tempCanvas.height)
        
        const delay = this.frameDelays[i] || 100
        gif.addFrame(tempCanvas, { copy: true, delay: delay })
        
        this.processProgress = Math.round((i + 1) / this.totalFrames * 50)
        
        // 每处理几帧让出主线程
        if (i % this.batchSize === 0) {
          await yieldToMain()
        }
      }
      
      gif.render()
    },
    async downloadGif() {
      if (!this.resultGifUrl) return
      await downloadFile(this.resultGifUrl, `${this.gifName}_text.gif`)
    },
    resetAll() {
      this.stopAnimation()
      
      if (this.gifUrl) {
        URL.revokeObjectURL(this.gifUrl)
      }
      if (this.resultGifUrl) {
        URL.revokeObjectURL(this.resultGifUrl)
      }
      if (this.gifWrapper && this.gifWrapper.parentNode) {
        this.gifWrapper.parentNode.removeChild(this.gifWrapper)
      }
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl)
      }
      
      this.gifUrl = ''
      this.gifFile = null
      this.gifName = ''
      this.hasGif = false
      this.frames = []
      this.frameDelays = []
      this.superGif = null
      this.gifWrapper = null
      this.blobUrl = null
      this.totalFrames = 0
      this.resultGifUrl = ''
      this.isProcessing = false
      this.processProgress = 0
      this.textContent = ''
      this.currentFrameIndex = 0
      this.isLoading = false
      
      if (this.$refs.gifInput) {
        this.$refs.gifInput.value = ''
      }
    }
  },
  beforeDestroy() {
    this.stopAnimation()
    if (this.gifUrl) {
      URL.revokeObjectURL(this.gifUrl)
    }
    if (this.resultGifUrl) {
      URL.revokeObjectURL(this.resultGifUrl)
    }
    if (this.gifWrapper && this.gifWrapper.parentNode) {
      this.gifWrapper.parentNode.removeChild(this.gifWrapper)
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl)
    }
  }
}
</script>

<style>
.text-wrapper {
  padding: 8px 0;
}

.text-wrapper .section-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2933;
}

.text-wrapper .drop-area {
  display: flex;
  flex-direction: column;
  padding: 60px 30px;
  min-height: 180px;
  border: 2px dashed #9fb3ff;
  border-radius: 12px;
  background: #f8f9ff;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: #4f46e5;
}

.text-wrapper .drop-area .size-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
  font-weight: 400;
  font-weight: 500;
}

.text-wrapper .drop-area.hover {
  border-color: #667eea;
  background: #e5edff;
}

.text-wrapper .drop-area input[type="file"] {
  display: none;
}

.text-wrapper .preview-section {
  background: #f8f9ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.text-wrapper .preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.text-wrapper .preview-title,
.text-wrapper .result-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.text-wrapper .btn-reselect {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
}

.text-wrapper .preview-canvas-wrapper {
  text-align: center;
}

.text-wrapper .preview-canvas {
  max-width: 100%;
  max-height: 250px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: move;
  touch-action: none;
}

.text-wrapper .preview-canvas.dragging {
  cursor: grabbing;
}

.text-wrapper .drag-hint {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 400;
}

.text-wrapper .text-options {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.text-wrapper .option-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
}

.text-wrapper .option-group {
  margin-bottom: 14px;
}

.text-wrapper .option-group.half {
  flex: 1;
}

.text-wrapper .option-row {
  display: flex;
  gap: 12px;
}

.text-wrapper .option-label {
  display: block;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 6px;
}

.text-wrapper .text-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
}

.text-wrapper .text-input:focus {
  outline: none;
  border-color: #667eea;
}

.text-wrapper .number-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

.text-wrapper .color-input {
  width: 100%;
  height: 38px;
  padding: 2px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
}

.text-wrapper .select-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
}

.text-wrapper .quick-position {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.text-wrapper .quick-label {
  font-size: 13px;
  color: #4b5563;
}

.text-wrapper .quick-btn {
  padding: 6px 12px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;
}

.text-wrapper .quick-btn:hover {
  background: #667eea;
  color: #fff;
  border-color: #667eea;
}

.text-wrapper .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 16px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
}

.text-wrapper .checkbox-label input {
  width: 16px;
  height: 16px;
}

.text-wrapper .action-row {
  display: flex;
  gap: 10px;
  margin: 16px 0;
}

.text-wrapper .btn-primary {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.text-wrapper .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-wrapper .btn-download {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: #28a745;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.text-wrapper .btn-download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-wrapper .progress-container {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9ff;
  border-radius: 8px;
}

.text-wrapper .progress-label {
  font-size: 13px;
  color: #4f46e5;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.text-wrapper .progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.text-wrapper .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.text-wrapper .result-section {
  margin-top: 16px;
  text-align: center;
}

.text-wrapper .result-title {
  margin-bottom: 12px;
}

.text-wrapper .result-gif {
  max-width: 100%;
  max-height: 300px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}
</style>

